import { useEffect, useState } from "react";
import axios from "axios";
import { Printer, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { printHTML } from "../../utils/printHTML";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

// ─── Arabic-words helper: same coarse implementation as InvoiceModal so the
// bulk-printed invoices display the SAME "فقط … دينار لا غير" line.
const _tens = ["", "عشرة", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"];
const _ones = ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة", "عشرة", "أحد عشر", "اثنا عشر", "ثلاثة عشر", "أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"];
const _hundreds = ["", "مئة", "مئتان", "ثلاثمئة", "أربعمئة", "خمسمئة", "ستمئة", "سبعمئة", "ثمانمئة", "تسعمئة"];
const numToArabicWords = (num) => {
  num = Math.floor(Number(num) || 0);
  if (num === 0) return "صفر";
  const parts = [];
  const billions = Math.floor(num / 1_000_000_000);
  const millions = Math.floor((num % 1_000_000_000) / 1_000_000);
  const thousands = Math.floor((num % 1_000_000) / 1_000);
  const rest = num % 1_000;
  const sub999 = (n) => {
    if (n === 0) return "";
    const out = [];
    const h = Math.floor(n / 100);
    const r = n % 100;
    if (h) out.push(_hundreds[h]);
    if (r < 20) { if (r) out.push(_ones[r]); }
    else {
      const t = Math.floor(r / 10);
      const o = r % 10;
      if (o) out.push(`${_ones[o]} و${_tens[t]}`);
      else out.push(_tens[t]);
    }
    return out.join(" و");
  };
  if (billions) parts.push(`${sub999(billions)} مليار`);
  if (millions) {
    if (millions === 1) parts.push("مليون");
    else if (millions === 2) parts.push("مليونان");
    else if (millions <= 10) parts.push(`${sub999(millions)} ملايين`);
    else parts.push(`${sub999(millions)} مليون`);
  }
  if (thousands) {
    if (thousands === 1) parts.push("ألف");
    else if (thousands === 2) parts.push("ألفان");
    else if (thousands <= 10) parts.push(`${sub999(thousands)} آلاف`);
    else parts.push(`${sub999(thousands)} ألف`);
  }
  if (rest) parts.push(sub999(rest));
  return `فقط ${parts.join(" و")} دينار لا غير`;
};

// Match InvoiceModal.formatDateTime exactly (HH:MM:SS DD/MM/YYYY)
const formatDateTimeLikeInvoice = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mi}:${ss} ${dd}/${mm}/${yyyy}`;
  } catch { return iso; }
};

// Resolve an item image URL — same rules as InvoiceModal.resolveImg
const resolveItemImg = (it) => {
  const raw = it.image_path || it.image_url || it.product_image || "";
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${raw}`;
  return `${API}/files/${raw}`;
};

// HTML-escape helper — avoids breaking the print HTML on names with < > &
const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// ─── Big red action: fetch ALL not-confirmed/not-printed orders, print all
// invoices in one shot, then flip their state to confirmed+printed. Handles
// both customer app orders (status: pending → confirmed) and supervisor
// sales awaiting admin confirmation.
// ─── Rebuild the bulk-print HTML to match InvoiceModal.jsx EXACTLY:
// one full-page invoice per order, wave-header + circular logo,
// meta table, حضرة السيد + عنوان الزبون + رقم الزبون rows, items table
// (with صورة column and empty-row padding), full totals table with
// "الدين المتبقي السابق" + "المجموع الكلي المطلوب", Arabic-words line,
// notes box (blank space for stamping), and the same footer.
export const buildBulkPrintHtml = (orders) => {
  const origin = window.location.origin;
  const logoUrl = `${origin}/brand/jumlaty-logo-round.png`;
  const waveUrl = `${origin}/brand/print-bg-wave.png`;
  const SUPPORT_PHONE = "07717637525";
  const SUPPORT_PHONE_2 = "07707662447";
  const COMPANY_NAME = "إدارة مكتب الأمين";
  const CITY = "كربلاء المقدسة";

  const pages = orders.map((o) => {
    const items = o.items || [];
    const invoiceNum = o.invoice_number != null
      ? String(o.invoice_number)
      : String(o.id || "").slice(0, 8).toUpperCase();
    const totalQty = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
    // Previous debt = current ledger − unpaid portion of THIS invoice.
    const orderUnpaid = Math.max(0, Number(o.amount_remaining ?? o.total ?? 0));
    const previousBalance = Math.max(0, Number(o.customer_debt || 0) - orderUnpaid);
    const grandTotal = Number(o.total || 0) + previousBalance;

    const itemRows = items.map((it, i) => {
      const img = resolveItemImg(it);
      return `
        <tr>
          <td class="b c">${i + 1}</td>
          <td class="b c img-cell">${img
            ? `<img src="${esc(img)}" class="item-img" onerror="this.style.display='none'"/>`
            : `<span class="dash">—</span>`}</td>
          <td class="b r">${esc(it.name_ar || it.name || it.product_name_ar || "")}</td>
          <td class="b c" dir="ltr">${fmt(it.quantity)}</td>
          <td class="b c" dir="ltr">${fmt(it.price)} د.ع</td>
          <td class="b c bold" dir="ltr">${fmt((it.price || 0) * (it.quantity || 0))} د.ع</td>
        </tr>`;
    }).join("");
    // Pad rows to at least 4 items — matches InvoiceModal
    const padCount = Math.max(0, 4 - items.length);
    const padRows = Array.from({ length: padCount }).map(() => `
      <tr>
        <td class="b pad">&nbsp;</td><td class="b pad">&nbsp;</td>
        <td class="b pad">&nbsp;</td><td class="b pad">&nbsp;</td>
        <td class="b pad">&nbsp;</td><td class="b pad">&nbsp;</td>
      </tr>`).join("");

    const discountRow = Number(o.manual_discount || 0) > 0 ? `
      <tr class="discount">
        <td class="b c bold" colspan="2">💰 الخصم على الفاتورة</td>
        <td class="b c bold discount-val" colspan="2" dir="ltr">−${fmt(o.manual_discount)} د.ع</td>
      </tr>` : "";

    return `
    <section class="invoice-page">
      <div class="invoice-inner">
        <!-- Circular watermark exactly like InvoiceModal -->
        <div class="watermark" aria-hidden="true">
          <img src="${logoUrl}" alt=""/>
        </div>
        <div class="content">
          <!-- Wave header w/ logo + support phones -->
          <div class="brand-header">
            <div class="brand-overlay"></div>
            <div class="brand-left">
              <img src="${logoUrl}" alt="جُملَتي" class="logo"/>
              <div class="brand-text">
                <p class="brand-name">جُملَتي</p>
                <p class="brand-sub">${COMPANY_NAME}</p>
              </div>
            </div>
            <div class="brand-right">
              <p class="support-label">للدعم والاستفسار</p>
              <p class="support-phone" dir="ltr">${SUPPORT_PHONE}</p>
              <p class="support-phone" dir="ltr">${SUPPORT_PHONE_2}</p>
            </div>
          </div>

          <!-- Invoice meta table -->
          <table class="meta">
            <tbody>
              <tr>
                <td class="b c bold w28">رقم القائمة</td>
                <td class="b c bold" dir="ltr">${esc(invoiceNum)}</td>
                <td class="c bold" colspan="2">قائمة بيع آجلة</td>
                <td class="b c bold w24">التاريخ</td>
                <td class="b c" dir="ltr">${formatDateTimeLikeInvoice(o.created_at)}</td>
              </tr>
              <tr>
                <td class="b c bold">حضرة السيد</td>
                <td class="b" colspan="5">${esc(o.customer_name || o.user_name || "")}${(o.customer_market_name || o.market_name) ? ` — ${esc(o.customer_market_name || o.market_name)}` : ""}</td>
              </tr>
              <tr>
                <td class="b c bold">عنوان الزبون</td>
                <td class="b" colspan="3">${esc(o.address || "—")}</td>
                <td class="b c bold">رقم الزبون</td>
                <td class="b c mono" dir="ltr">${esc(o.customer_phone || o.user_phone || "")}</td>
              </tr>
            </tbody>
          </table>

          <!-- Items table -->
          <table class="items">
            <thead>
              <tr>
                <th class="b c w10">ت</th>
                <th class="b c w14">صورة</th>
                <th class="b r">المادة</th>
                <th class="b c w20">العدد</th>
                <th class="b c w32">السعر</th>
                <th class="b c w32">المجموع</th>
              </tr>
            </thead>
            <tbody>
              ${itemRows}
              ${padRows}
            </tbody>
          </table>

          <!-- Totals table -->
          <table class="totals">
            <tbody>
              <tr>
                <td class="b c bold w28">المباع الواصل</td>
                <td class="b c" dir="ltr">0</td>
                <td class="b c bold w28">قيمة هذه الفاتورة</td>
                <td class="b c bold" dir="ltr">${fmt(o.total)} د.ع</td>
              </tr>
              <tr>
                <td class="b c bold">عدد الكراتين</td>
                <td class="b c bold" dir="ltr">${totalQty}</td>
                <td class="b c bold">الدين المتبقي السابق</td>
                <td class="b c bold" dir="ltr">${fmt(previousBalance)} د.ع</td>
              </tr>
              ${discountRow}
              <tr class="grand">
                <td class="b c black" colspan="2">المجموع الكلي المطلوب</td>
                <td class="b c black big" colspan="2" dir="ltr">${fmt(grandTotal)} د.ع</td>
              </tr>
            </tbody>
          </table>

          <!-- Amount in Arabic words -->
          <p class="words">${numToArabicWords(o.total)}</p>

          <!-- Notes / stamp area (empty space intentionally for signature/stamp) -->
          <div class="notes">
            <p class="notes-label">الملاحظات</p>
            <p class="notes-body">${esc(o.notes || "")}</p>
          </div>

          <!-- Footer -->
          <div class="foot">
            <span>1 من صفحة 1</span>
            <span>${COMPANY_NAME} — العراق — ${CITY} — ${SUPPORT_PHONE} — ${SUPPORT_PHONE_2}</span>
            <span>www.jumlaty.online</span>
          </div>
        </div>
      </div>
    </section>`;
  }).join("");

  return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"/>
    <title>طباعة جماعية — ${orders.length} فاتورة</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;900&display=swap" rel="stylesheet">
    <style>
      @page { size: A4 portrait; margin: 5mm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      html, body {
        margin: 0; padding: 0; background: #fff; color: #000;
        font-family: 'Tajawal','Cairo',system-ui,-apple-system,sans-serif;
      }
      /* Each invoice = its own A4 page (like a single printed invoice) */
      .invoice-page { page-break-after: always; }
      .invoice-page:last-child { page-break-after: auto; }
      .invoice-inner {
        position: relative; overflow: hidden;
        padding: 8mm 6mm;
        width: 100%; max-width: 200mm; margin: 0 auto;
        font-size: 10pt; line-height: 1.35; color: #000; background: #fff;
      }
      /* Center watermark — same subtle circular logo as InvoiceModal */
      .watermark {
        position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
        pointer-events: none; z-index: 0; overflow: hidden;
      }
      .watermark img { width: 70%; max-width: 600px; height: auto; opacity: 0.18; }
      .content { position: relative; z-index: 1; }

      /* Brand header w/ wave background */
      .brand-header {
        position: relative;
        background-image: url('${waveUrl}');
        background-size: cover; background-position: center; background-repeat: no-repeat;
        border: 2px solid #1E3A5F; border-radius: 8px;
        padding: 10px 12px; margin-bottom: 6px;
        display: flex; align-items: center; justify-content: space-between; gap: 12px;
        min-height: 100px; overflow: hidden;
      }
      .brand-overlay {
        position: absolute; inset: 0;
        background: linear-gradient(90deg, rgba(255,255,255,0.18), rgba(255,255,255,0.05));
        pointer-events: none;
      }
      .brand-left { display: flex; align-items: center; gap: 10px; position: relative; z-index: 2; }
      .brand-left .logo {
        width: 74px; height: 74px; border-radius: 50%;
        border: 2px solid rgba(255,255,255,0.6);
        box-shadow: 0 2px 6px rgba(0,0,0,0.25); object-fit: cover;
      }
      .brand-text { display: flex; flex-direction: column; }
      .brand-name {
        font-size: 26px; font-weight: 900; color: #fff; margin: 0; line-height: 1;
        text-shadow: 0 2px 6px rgba(0,0,0,0.45);
      }
      .brand-sub {
        font-size: 12px; color: #fff; font-weight: 700; margin: 8px 0 0;
        text-shadow: 0 1px 3px rgba(0,0,0,0.5);
      }
      .brand-right { text-align: right; position: relative; z-index: 2; }
      .support-label {
        font-size: 11px; font-weight: 800; color: #fff; margin: 0;
        text-shadow: 0 1px 3px rgba(0,0,0,0.5);
      }
      .support-phone {
        font-family: 'Courier New', monospace; font-weight: 800;
        font-size: 15px; color: #fff; margin: 2px 0 0;
        text-shadow: 0 1px 3px rgba(0,0,0,0.5);
      }

      /* Meta / items / totals tables — same visual as InvoiceModal */
      table.meta, table.items, table.totals {
        width: 100%; border-collapse: collapse; margin-bottom: 4px; color: #000;
        font-size: 9.5pt;
      }
      table.meta td, table.items th, table.items td, table.totals td {
        padding: 3px 4px; vertical-align: middle;
      }
      .b { border: 1px solid #000; }
      .c { text-align: center; }
      .r { text-align: right; }
      .bold { font-weight: 800; }
      .black { font-weight: 900; }
      .big { font-size: 12pt; }
      .mono { font-family: 'Courier New', monospace; }
      .w10 { width: 32px; } .w14 { width: 50px; }
      .w20 { width: 60px; } .w24 { width: 88px; } .w28 { width: 100px; } .w32 { width: 110px; }
      .img-cell { padding: 2px !important; }
      .item-img {
        width: 12mm; height: 12mm; object-fit: cover;
        display: block; margin: 0 auto; border-radius: 2px;
      }
      .dash { color: #9CA3AF; font-size: 10pt; }
      .pad { padding: 10px 4px !important; }

      table.totals .grand { background: #FEF08A; }
      table.totals .discount { background: #DCFCE7; }
      table.totals .discount .discount-val { color: #166534; }

      /* Amount in words */
      .words {
        text-align: center; font-size: 9.5pt; font-weight: 800;
        border: 1px solid #000; border-radius: 4px; padding: 6px;
        margin: 4px 0 6px; color: #000;
      }
      /* Notes / stamping space — matches InvoiceModal's border-t block */
      .notes {
        border-top: 1px solid #000; padding-top: 6px; margin-bottom: 10px;
      }
      .notes-label { font-size: 9pt; font-weight: 800; margin: 0 0 4px; color: #000; }
      .notes-body {
        font-size: 8pt; color: #000; margin: 0;
        min-height: 40px; /* empty space for stamp/signature */
      }
      /* Footer — 3 columns, page indicator, company info, website */
      .foot {
        display: flex; justify-content: space-between; align-items: center;
        border-top: 1px solid #000; padding-top: 4px;
        font-size: 8.5pt; font-weight: 800; color: #000;
      }
      @media print {
        .invoice-page { page-break-after: always; }
        .invoice-page:last-child { page-break-after: auto; }
      }
    </style></head><body>${pages}
    </body></html>`;
};

const BulkConfirmPrintButton = () => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Preview state: full list of pending invoices + selected ids set
  const [previewOrders, setPreviewOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewLoading, setPreviewLoading] = useState(false);

  const refreshCount = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders/pending-for-bulk-print`);
      setCount(data?.count || 0);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  };

  const openPreview = async () => {
    setConfirmOpen(true);
    setPreviewLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders/pending-for-bulk-print`);
      const orders = data?.orders || [];
      setPreviewOrders(orders);
      // Default: all selected
      setSelectedIds(new Set(orders.map((o) => o.id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل جلب الفواتير");
      setPreviewOrders([]);
    } finally { setPreviewLoading(false); }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(previewOrders.map((o) => o.id)));
  const deselectAll = () => setSelectedIds(new Set());

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 30000);
    return () => clearInterval(id);
  }, []);


  const doIt = async () => {
    if (selectedIds.size === 0) {
      toast.error("لم يتم اختيار أي فاتورة");
      return;
    }
    setBusy(true);
    try {
      // Filter to selected orders only
      const orders = previewOrders.filter((o) => selectedIds.has(o.id));
      if (orders.length === 0) { toast.info("لا توجد فواتير مختارة"); setBusy(false); return; }
      // 1) Render + print via hidden iframe (bypasses popup blocker on Windows Chrome/Edge)
      printHTML(buildBulkPrintHtml(orders), { title: "طباعة الفواتير" });
      // 2) Mark selected as confirmed + printed
      const ids = orders.map((o) => o.id);
      const { data: res } = await axios.post(`${API}/admin/orders/bulk-confirm-and-mark-printed`, { order_ids: ids });
      toast.success(
        `✓ تم تأكيد ${res.customer_orders_confirmed} فاتورة زبون + ${res.supervisor_orders_confirmed} فاتورة مشرف · تم إرسال ${orders.length} فاتورة للطابعة`,
        { duration: 6000 }
      );
      setConfirmOpen(false);
      setTimeout(refreshCount, 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشلت العملية");
    } finally { setBusy(false); }
  };

  if (count === 0 && !loading) return null;

  return (
    <>
      <button
        onClick={openPreview}
        disabled={busy || count === 0}
        data-testid="bulk-confirm-print-btn"
        className="w-full mb-3 bg-gradient-to-l from-[#B91C1C] via-[#DC2626] to-[#E85D45] hover:brightness-110 text-white font-black rounded-2xl py-4 px-4 shadow-xl transition-all flex items-center gap-3 disabled:opacity-60 border-4 border-[#FEE2E2]"
      >
        <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center shrink-0">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Printer className="w-6 h-6" />}
        </div>
        <div className="flex-1 text-right">
          <p className="text-lg font-black flex items-center gap-2">
            🖨️ تأكيد وطباعة الكل
            <span className="bg-white text-[#B91C1C] rounded-full px-2 py-0.5 text-xs font-black" dir="ltr">
              {loading ? "..." : count}
            </span>
          </p>
          <p className="text-[11px] opacity-95">
            اطبع كل الفواتير غير المؤكّدة (زبائن + مشرفين) دفعة واحدة وثبّتها كمؤكّدة
          </p>
        </div>
        <CheckCircle2 className="w-6 h-6 opacity-80 shrink-0" />
      </button>

      {confirmOpen && (
        <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => !busy && setConfirmOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()} data-testid="bulk-confirm-modal">
            {/* Header */}
            <div className="p-4 border-b border-[#E4EBE6] bg-gradient-to-l from-[#B91C1C] to-[#E85D45] text-white rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center">
                  <Printer className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-black text-lg">معاينة قبل التأكيد الجماعي</h3>
                  <p className="text-[11px] opacity-90">
                    شيّك على الفواتير اللي تريد تأكيدها وطباعتها — شيل أي فاتورة مو جاهزة
                  </p>
                </div>
              </div>
            </div>

            {/* Toolbar: select all / deselect all + count */}
            <div className="px-4 py-2 border-b border-[#E4EBE6] bg-[#F8FAFC] flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-[#1E3A5F]">
                محدد: <span className="text-[#B91C1C] font-black">{fmt(selectedIds.size)}</span> من <span dir="ltr">{fmt(previewOrders.length)}</span>
              </span>
              <div className="ml-auto flex gap-1">
                <button onClick={selectAll} data-testid="bulk-select-all"
                  className="text-[10px] bg-[#DBEAFE] hover:bg-[#BFDBFE] text-[#1E40AF] rounded-full px-2 py-1 font-bold">
                  ✓ اختر الكل
                </button>
                <button onClick={deselectAll} data-testid="bulk-deselect-all"
                  className="text-[10px] bg-[#FEE2E2] hover:bg-[#FECACA] text-[#B91C1C] rounded-full px-2 py-1 font-bold">
                  ✗ إلغاء الاختيار
                </button>
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="bulk-preview-list">
              {previewLoading ? (
                <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin inline text-[#B91C1C]" /></div>
              ) : previewOrders.length === 0 ? (
                <div className="text-center py-8 text-[#5C6E64]">
                  <p className="text-sm">لا توجد فواتير غير مؤكّدة الآن</p>
                </div>
              ) : previewOrders.map((o) => {
                const selected = selectedIds.has(o.id);
                const src = o.is_supervisor_sale
                  ? { label: `${o.seller_role === "mandoob" ? "المندوب" : "المشرف"}${o.supervisor_name ? ` — ${o.supervisor_name}` : ""}`, cls: "bg-[#EDE9FE] text-[#6D28D9]" }
                  : o.is_direct_sale
                  ? { label: "بيع مباشر", cls: "bg-[#FFF8EC] text-[#a06e10]" }
                  : { label: "تطبيق", cls: "bg-[#DBEAFE] text-[#1E40AF]" };
                return (
                  <label
                    key={o.id}
                    data-testid={`bulk-preview-row-${o.id}`}
                    className={`flex items-center gap-2 p-2 rounded-xl border-2 cursor-pointer transition-all ${
                      selected
                        ? "bg-[#FEF3C7] border-[#F5A623]"
                        : "bg-white border-[#E4EBE6] opacity-70"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleOne(o.id)}
                      data-testid={`bulk-check-${o.id}`}
                      className="w-5 h-5 accent-[#B91C1C] shrink-0"
                    />
                    <div className="bg-[#1E3A5F] text-white rounded-lg px-2 py-1 text-center shrink-0" style={{minWidth: 48}}>
                      <p className="text-[9px] opacity-80 leading-none">فاتورة</p>
                      <p className="text-sm font-black leading-none mt-0.5" dir="ltr">#{o.invoice_number || "—"}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-bold text-sm text-[#111814] truncate">{o.customer_name || "زبون بدون اسم"}</p>
                        <span className={`text-[9px] font-bold rounded-full px-1.5 py-0.5 ${src.cls}`}>{src.label}</span>
                      </div>
                      <p className="text-[10px] text-[#5C6E64] truncate" dir="ltr">
                        {o.customer_market_name && <>{o.customer_market_name} · </>}
                        {fmtDate(o.created_at)} · {(o.items || []).length} صنف
                      </p>
                    </div>
                    <div className="text-left shrink-0">
                      <p className="text-[9px] text-[#5C6E64]">الإجمالي</p>
                      <p className="font-black text-sm text-[#1E3A5F]" dir="ltr">{fmt(o.total)}</p>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Selected summary */}
            {selectedIds.size > 0 && (
              <div className="px-4 py-2 border-t border-[#E4EBE6] bg-[#FEF3C7] text-[11px] text-[#B45309] text-center">
                ⚠ راح يتم طباعة <b>{fmt(selectedIds.size)}</b> فاتورة وتحويلها لمؤكّدة —
                إجمالي المبيعات: <b dir="ltr">{fmt(previewOrders.filter(o => selectedIds.has(o.id)).reduce((s, o) => s + Number(o.total || 0), 0))} د.ع</b>
              </div>
            )}

            {/* Actions */}
            <div className="p-3 border-t border-[#E4EBE6] flex gap-2 rounded-b-2xl">
              <button onClick={() => setConfirmOpen(false)} disabled={busy} data-testid="bulk-confirm-cancel"
                className="flex-1 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] rounded-xl py-3 font-bold">
                إلغاء
              </button>
              <button onClick={doIt} disabled={busy || selectedIds.size === 0} data-testid="bulk-confirm-yes"
                className="flex-[2] bg-gradient-to-l from-[#B91C1C] to-[#E85D45] hover:brightness-110 text-white rounded-xl py-3 font-black flex items-center justify-center gap-1 disabled:opacity-60">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>🖨️ تأكيد وطباعة {fmt(selectedIds.size)} فاتورة</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkConfirmPrintButton;
